import { episodes, type Episode } from "@/mocks/episodes";
import { articles, type Article } from "@/mocks/articles";

export function getEpisodeById(id: string): Episode | undefined {
  return episodes.find((item) => item.id === id);
}

export function getArticleById(id: string): Article | undefined {
  return articles.find((item) => item.id === id);
}

export function getArticlesByIds(ids: string[]): Article[] {
  return ids
    .map((id) => getArticleById(id))
    .filter((item): item is Article => Boolean(item));
}

export function sortByDateDesc<T extends { date: string }>(list: T[]): T[] {
  return [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
}

export function sortByDateAsc<T extends { date: string }>(list: T[]): T[] {
  return [...list].sort((a, b) => (a.date > b.date ? 1 : -1));
}

export function formatDate(date: string): string {
  const parts = date.split("-");
  if (parts.length !== 3) return date;
  return `${parts[0]} / ${parts[1]} / ${parts[2]}`;
}

export function getEpisodeTypes(): string[] {
  const set = new Set(episodes.map((item) => item.type));
  return Array.from(set);
}

export function getRelatedEpisodes(episode: Episode, limit = 3): Episode[] {
  const others = episodes.filter((item) => item.id !== episode.id);
  const sameType = others.filter((item) => item.type === episode.type);
  const sameTag = others.filter(
    (item) => item.type !== episode.type && item.tags.some((tag) => episode.tags.includes(tag)),
  );
  return [...sameType, ...sameTag].slice(0, limit);
}

export type SearchResult = {
  id: string;
  kind: "episode" | "article";
  title: string;
  summary: string;
  cover: string;
  date: string;
  link: string;
  badge: string;
};

export function buildSearchIndex(): SearchResult[] {
  const episodeResults: SearchResult[] = episodes.map((item) => ({
    id: item.id,
    kind: "episode",
    title: item.title,
    summary: item.summary,
    cover: item.cover,
    date: item.date,
    link: `/detail/${item.id}`,
    badge: item.type,
  }));
  const articleResults: SearchResult[] = articles.map((item) => ({
    id: item.id,
    kind: "article",
    title: item.title,
    summary: item.summary,
    cover: item.cover,
    date: item.date,
    link: `/special?article=${item.id}`,
    badge: item.category,
  }));
  return [...episodeResults, ...articleResults];
}

export function searchContent(
  keyword: string,
  kind: "all" | "episode" | "article",
): SearchResult[] {
  const trimmed = keyword.trim().toLowerCase();
  if (!trimmed) return [];
  return buildSearchIndex().filter((item) => {
    if (kind !== "all" && item.kind !== kind) return false;
    const haystack = `${item.title} ${item.summary} ${item.badge}`.toLowerCase();
    return haystack.includes(trimmed);
  });
}

export function getHotEpisodes(limit = 6): Episode[] {
  return sortByDateDesc(episodes).slice(0, limit);
}

export function getLatestArticles(limit = 5): Article[] {
  return sortByDateDesc(articles).slice(0, limit);
}